import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { GetBasketQuery } from '@generated/gql/graphql';
import { SnackbarVariant, useSnackbar } from '@hooks/useSnackbar';
import { useAuthContext } from '@services/Auth';
import { useSetMode } from '@store/journey/selectors';
import { JOURNEY_MODE } from '@store/journey/slice';
import { clearFormPersistStorage } from '@utils/storagePropertyManagement';

import { useBasketContext } from '../providers/basket/BasketProvider/Context';
import { useDetailsContext } from '../providers/basket/DetailsProvider/Context';

interface UseDetectMidJourneyLoginParams {
  basketData: GetBasketQuery | null | undefined;
  loading?: boolean;
}

export const useDetectMidJourneyLogin = ({
  basketData,
  loading = false,
}: UseDetectMidJourneyLoginParams) => {
  const { activeClient } = useAuthContext();
  const { basketId, handleStartAgain } = useBasketContext();
  const { resetDetails } = useDetailsContext();
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const setMode = useSetMode();
  const hasResetRef = useRef(false);

  useEffect(() => {
    if (loading || hasResetRef.current) {
      return;
    }

    const isAuthenticated = !!activeClient;
    const hasBasket = !!basketId;
    const hasAttributionData = !!basketData;
    const hasAssistant = !!basketData?.attributionDetails?.assistant;

    if (isAuthenticated && hasBasket && hasAttributionData && !hasAssistant) {
      hasResetRef.current = true;

      showSnackbar({
        message:
          'We detected an invalid session state and restarted your quote.',
        variant: SnackbarVariant.Info,
        preventDuplicate: true,
      });

      resetDetails();
      handleStartAgain();
      clearFormPersistStorage();
      setMode(JOURNEY_MODE.SIGNUP);
    }
  }, [
    activeClient,
    basketId,
    basketData,
    loading,
    showSnackbar,
    resetDetails,
    handleStartAgain,
    navigate,
  ]);
};
